import DateFnsUtils from '@date-io/date-fns';
import React, { useContext, useState } from 'react';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import ArrowLeft from '@material-ui/icons/ArrowBack';
import ArrowRight from '@material-ui/icons/ArrowForward';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { DatePicker } from '@material-ui/pickers/DatePicker';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import {
  ApiFilterOperation,
  NavigationContext,
  UserContext,
  UserSettingsContext,
} from '@eas/common-web';
import { useColumns as useCourtFilesAllColumns } from '@modules/es-file-court/all/court-file-all-columns';
import {
  COURT_FILES_ALL_IDENTIFIER,
  COURT_FILES_ALL_VERSION,
} from '@modules/es-file-court/all/court-files-all';
import { useColumns as useDisciplinaryProceedingsFilesColumns } from '@modules/es-file-disciplinary-proceedings/disciplinary-proceedings-file-columns';
import {
  DISCIPLINARY_PROCEEDINGS_FILES_IDENTIFIER,
  DISCIPLINARY_PROCEEDINGS_FILES_VERSION,
} from '@modules/es-file-disciplinary-proceedings/disciplinary-proceedings-files';
import { useColumns as usePlenaryOpinionFilesColumns } from '@modules/es-file-plenary-opinion/plenary-opinion-file-columns';
import {
  PLENARY_OPINION_FILES_IDENTIFIER,
  PLENARY_OPINION_FILES_VERSION,
} from '@modules/es-file-plenary-opinion/plenary-opinion-files';
import { Me } from '@models';
import { Agenda, EvidenceBrowserUrl } from '@enums';
import { DashboardBarChart, DashboardBarChartItem } from '../bar-chart';
import { DashboardButton } from '../button';
import { DashboardCard } from '../card';
import {
  FormattedEsFilesBar,
  useJudgeEsFilesStatistics,
} from '../dashboard-hook';
import { DashboardDivider } from '../divider';
import {
  createInitialFilterState,
  fetchJudgeAutocomplete,
  getCurrentUserJudge,
  getFilterStateIndexFromColumnsByKey,
  resolvedEssRecordStates,
  unresolvedEssRecordStates,
} from './utils';

const agendas = [
  Agenda.COURT,
  Agenda.PLENARY_OPINION,
  Agenda.DISCIPLINARY_PROCEEDINGS,
] as const;

const MAX_YEAR = new Date().getFullYear();
const MIN_YEAR = 1990;

const getAgendaLabel = (a: typeof agendas[number]) => {
  switch (a) {
    case Agenda.COURT:
      return { buttonLabel: 'Soudní', title: 'Moje soudní spisy' };
    case Agenda.PLENARY_OPINION:
      return {
        buttonLabel: 'Stanoviska pléna',
        title: 'Moje stanovsika pléna',
      };
    case Agenda.DISCIPLINARY_PROCEEDINGS:
      return {
        buttonLabel: 'Kárné řízení',
        title: 'Moje kárné řízení',
      };
    default:
      return { buttonLabel: '?', title: '?' };
  }
};

export function JudgeMyEsFilesStatistics() {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const { navigate } = useContext(NavigationContext);
  const { getTableSettings, setTableSettings } =
    useContext(UserSettingsContext);

  const [year, setYear] = useState(MAX_YEAR);
  const [allYears, setAllYears] = useState(true);
  const [agenda, setAgenda] = useState<typeof agendas[number]>(agendas[0]);
  const [open, setOpen] = useState(false);

  const { formattedData } = useJudgeEsFilesStatistics(
    allYears ? undefined : year
  );

  const courtFilesAllColumns = useCourtFilesAllColumns();
  const plenaryOpinionFilesColumns = usePlenaryOpinionFilesColumns();
  const disciplinaryProceedingsFilesColumns =
    useDisciplinaryProceedingsFilesColumns();

  const evidenceConfigByAgenda = {
    [Agenda.COURT]: {
      identifier: COURT_FILES_ALL_IDENTIFIER,
      version: COURT_FILES_ALL_VERSION,
      columns: courtFilesAllColumns,
      url: EvidenceBrowserUrl.COURT_FILES_ALL,
    },
    [Agenda.PLENARY_OPINION]: {
      identifier: PLENARY_OPINION_FILES_IDENTIFIER,
      version: PLENARY_OPINION_FILES_VERSION,
      columns: plenaryOpinionFilesColumns,
      url: EvidenceBrowserUrl.PLENARY_OPINION_FILES,
    },
    [Agenda.DISCIPLINARY_PROCEEDINGS]: {
      identifier: DISCIPLINARY_PROCEEDINGS_FILES_IDENTIFIER,
      version: DISCIPLINARY_PROCEEDINGS_FILES_VERSION,
      columns: disciplinaryProceedingsFilesColumns,
      url: EvidenceBrowserUrl.DISCIPLINARY_PROCEEDINGS_FILES,
    },
  };

  const handleBarClick = async (
    _barData: DashboardBarChartItem,
    index?: number
  ) => {
    const agendaData = formattedData?.data?.[agenda];

    const barData = _barData as FormattedEsFilesBar;

    if (index === undefined || index < 0 || !agendaData || !barData) {
      return;
    }

    const config = evidenceConfigByAgenda[agenda];
    const tableSettingsId = `${config.identifier}_TABLE`;
    const settings = getTableSettings(tableSettingsId, config.version);
    const filtersState = createInitialFilterState(config.columns);
    const judge = getCurrentUserJudge(user);

    const setFilterIfExists = (
      datakey: string,
      filterState: typeof filtersState[number]
    ) => {
      const filterIndex = getFilterStateIndexFromColumnsByKey(
        config.columns,
        datakey
      );

      if (filterIndex !== -1) {
        filtersState[filterIndex] = filterState;
      }
    };

    const essStates =
      barData.state === 'RESOLVED'
        ? resolvedEssRecordStates
        : unresolvedEssRecordStates;

    const essRecordStateFilter = {
      enabled: true,
      value: null,
      operation: ApiFilterOperation.OR,
      filters: essStates.map((value) => ({
        field: 'essRecord.state.id',
        operation: ApiFilterOperation.EQ,
        value,
      })),
      selectFilter: essStates,
      object: null,
      numberFilter: null,
    };

    if (!allYears) {
      setFilterIfExists('number.year', {
        enabled: true,
        value: null,
        operation: ApiFilterOperation.AND,
        object: null,
        filters: [
          {
            field: 'number.year',
            operation: ApiFilterOperation.GTE,
            value: year,
          },
          {
            field: 'number.year',
            operation: ApiFilterOperation.LTE,
            value: year,
          },
        ],
        numberFilter: {
          from: year,
          to: year,
        },
      });
    }

    if (judge.id) {
      setFilterIfExists('judge.id', {
        enabled: true,
        value: judge.id,
        operation: ApiFilterOperation.EQ,
        object: await fetchJudgeAutocomplete(judge),
      });
    }

    setFilterIfExists('essRecord.state.id', essRecordStateFilter);

    if (agenda === Agenda.COURT) {
      setFilterIfExists('type.id', {
        enabled: true,
        value: barData.courtFileType,
        operation: ApiFilterOperation.EQ,
      });
    }

    setTableSettings(tableSettingsId, {
      ...settings,
      version: config.version,
      searchQuery: '',
      filtersState,
    });

    navigate(config.url);
  };
  const isEmpty = () => {
    return !agendas.some(
      (a) => (formattedData?.data?.[a]?.totalCount ?? 0) > 0
    );
  };

  return (
    <>
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Grid item xs={12}>
          <DashboardCard>
            <Button
              onClick={() => setYear((p) => p - 1)}
              disabled={allYears || year === MIN_YEAR}
            >
              <ArrowLeft />
            </Button>
            <DatePicker
              views={['year']}
              value={new Date(year, 0, 1)}
              minDate={new Date(MIN_YEAR, 0, 1)}
              maxDate={new Date(MAX_YEAR, 0, 1)}
              onChange={(v) => {
                setYear(v?.getFullYear?.() ?? MIN_YEAR);
              }}
              open={allYears ? false : open}
              openTo="date"
              onClose={() => setOpen(false)}
              TextFieldComponent={function Input() {
                return (
                  <Button
                    variant="outlined"
                    disabled={allYears}
                    onClick={() => !allYears && setOpen(true)}
                    endIcon={<CalendarTodayIcon fontSize="small" />}
                  >
                    {year}
                  </Button>
                );
              }}
            />

            <Button
              onClick={() => setYear((p) => p + 1)}
              disabled={allYears || year === MAX_YEAR}
            >
              <ArrowRight />
            </Button>

            <FormControlLabel
              control={
                <Switch
                  checked={allYears}
                  color="primary"
                  size="small"
                  onChange={(event) => setAllYears(event.target.checked)}
                />
              }
              label="Všechny roky"
              labelPlacement="end"
            />

            <Grid container spacing={3}>
              {isEmpty() ? (
                <Grid
                  item
                  xs={12}
                  style={{
                    placeSelf: 'center',
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography>Žádné spisy</Typography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={5} style={{ placeSelf: 'center' }}>
                    {agendas?.map((a) => (
                      <DashboardButton
                        key={a}
                        label={getAgendaLabel(a).buttonLabel}
                        value={formattedData?.data?.[a]?.totalCount ?? 0}
                        active={agenda === a}
                        showAction={true}
                        onClick={() => setAgenda(a)}
                      />
                    ))}
                  </Grid>
                  <Grid item xs={7}>
                    <DashboardDivider
                      text={`${getAgendaLabel(agenda).title} - ${
                        allYears ? 'všechny roky' : year
                      }`}
                    />

                    <DashboardBarChart
                      data={[
                        ...(formattedData?.data?.[agenda]?.unresolved ?? []),
                        ...(formattedData?.data?.[agenda]?.resolved ?? []),
                      ]}
                      onBarClick={handleBarClick}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DashboardCard>
        </Grid>
      </MuiPickersUtilsProvider>
    </>
  );
}
